<script setup>
import ToggleAlertProcessedAtAction from "@frontJs/components/actions/ToggleAlertProcessedAtAction.vue";
import SaveAppointmentFrequencyAction from "@frontJs/components/actions/projectManagement/apikom/SaveAppointmentFrequencyAction.vue";
import ChatCustomerTaskAction from "@frontJs/components/actions/projectManagement/tasks/ChatCustomerTaskAction.vue";
import ToggleCustomerTaskDoneAtAction from "@frontJs/components/actions/projectManagement/tasks/ToggleCustomerTaskDoneAtAction.vue";
import ToggleCustomerTaskValidatedAction from "@frontJs/components/actions/projectManagement/tasks/ToggleCustomerTaskValidatedAction.vue";
import {
  ChatBubbleBottomCenterTextIcon,
  CheckIcon,
  ClockIcon, EnvelopeIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon
} from "@heroicons/vue/24/outline";
import ActionButton from "@js/components/ActionButton.vue";
import { useRoute } from "@js/utils/route";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import SendEmailAction from "@js/components/actions/SendEmailAction.vue";

const {
  row,
  callback,
  group = undefined,
  type = undefined,
} = defineProps({
  row: {
    type: Object,
    required: true,
  },
  callback: {
    type: Function,
    required: false,
  },
  group: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: false,
  },
});

const { t } = useI18n();

const { route } = useRoute();

function sendActionCallback(from, to, subject, body, setLoading, setIsOpen) {
  fetch(
    route("front.projectManagement.apikom.postSendTenantAppointmentEmail", {
      tenant: row.original.data.tenantId,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        from: from,
        to: to,
        subject: subject,
        body: body,
      }),
    },
  )
    .then(() => {
      setLoading(false);
      setIsOpen(false);
      toast.success(t("toast.successfullySent"), { position: "top-right" });
      callback();
    })
    .catch((error) => {
      console.error(error);
    });
}
</script>

<template>
  <div class="flex h-full items-center gap-2 p-4">
    <ToggleAlertProcessedAtAction
      v-if="
        (!group && !type) ||
        group === 'sea' ||
        group === 'sma' ||
        type === 'dead_netlinking_link'
      "
      :alert="row.original"
      :callback="
        () => {
          toast.success(t('toast.successfullySaved'), {
            position: 'top-right',
          });

          if (callback) {
            callback();
          }
        }
      "
    >
      <template #button="{ action, loading }">
        <ActionButton
          size="small"
          class="size-8"
          :title="
            row.original.processedAt === null
              ? t('button.processAlert')
              : t('button.unprocessAlert')
          "
          type="secondary"
          :callback="() => action()"
          :icon="row.original.processedAt === null ? CheckIcon : XMarkIcon"
          :loading="loading"
        />
      </template>
    </ToggleAlertProcessedAtAction>

    <ActionButton
      v-if="
        ((!group && !type) ||
          group === 'sea' ||
          group === 'sma' ||
          type === 'dead_netlinking_link') &&
        row.original.url
      "
      size="small"
      class="size-8"
      :title="t('button.see')"
      :href="row.original.url"
      :icon="EyeIcon"
    />

    <template v-if="group === 'tasks'">
      <ToggleCustomerTaskValidatedAction
        v-if="
          (row.original.type.value === 'unread_customer_task_answer' ||
            row.original.type.value === 'customer_task_to_validate') &&
          row.original.data.tenantTaskDoneAt !== null
        "
        :tenant-task="{ id: row.original.data.tenantTaskId }"
        :callback="
          () => {
            toast.success(t('toast.successfullySaved'), {
              position: 'top-right',
            });

            if (callback) {
              callback();
            }
          }
        "
      >
        <template #button="{ action, loading }">
          <ActionButton
            size="small"
            class="size-8"
            :title="t('button.validateCustomerTask')"
            type="secondary"
            :callback="() => action()"
            :icon="CheckIcon"
            :loading="loading"
          />
        </template>
      </ToggleCustomerTaskValidatedAction>

      <ToggleCustomerTaskDoneAtAction
        v-if="
          (row.original.type.value === 'unread_customer_task_answer' ||
            row.original.type.value === 'customer_task_to_validate') &&
          row.original.data.tenantTaskDoneAt !== null
        "
        :tenant-task="{ id: row.original.data.tenantTaskId }"
        :callback="
          () => {
            toast.success(t('toast.successfullySaved'), {
              position: 'top-right',
            });

            if (callback) {
              callback();
            }
          }
        "
      >
        <template #button="{ action, loading }">
          <ActionButton
            size="small"
            class="size-8"
            :title="t('button.invalidateCustomerTask')"
            type="secondary"
            :callback="() => action()"
            :icon="XMarkIcon"
            :loading="loading"
          />
        </template>
      </ToggleCustomerTaskDoneAtAction>

      <ActionButton
        v-if="
          row.original.type.value === 'unread_customer_task_answer' ||
          row.original.type.value === 'customer_task_to_validate'
        "
        size="small"
        class="size-8"
        :title="t('button.edit')"
        :href="
          route('front.projectManagement.task.customerTask', {
            id: row.original.data.tenantTaskId,
          })
        "
        :icon="PencilIcon"
      />

      <ChatCustomerTaskAction
        v-if="
          row.original.type.value === 'unread_customer_task_answer' ||
          row.original.type.value === 'customer_task_to_validate'
        "
        :tenant-task="{
          id: row.original.data.tenantTaskId,
          orderedAnswers: row.original.data.tenantTaskOrderedAnswers,
        }"
        :callback="callback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            type="primary"
            class="size-8"
            :callback="() => setIsOpen(true)"
            :title="t('button.chat')"
            :icon="ChatBubbleBottomCenterTextIcon"
            :ping="row.original.data.tenantTaskHasNewCustomerAnswer"
          />
        </template>
      </ChatCustomerTaskAction>
    </template>

    <template v-if="type === 'appointment_to_make'">
      <SaveAppointmentFrequencyAction
        :tenant="{
          id: row.original.data.tenantId,
          name: row.original.data.tenantName,
          appointmentFrequency: row.original.data.tenantAppointmentFrequency,
        }"
        :callback="
          () => {
            toast.success(t('toast.successfullySaved'), {
              position: 'top-right',
            });

            if (callback) {
              callback();
            }
          }
        "
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            class="size-8"
            type="secondary"
            :title="t('button.updateAppointmentFrequency')"
            :callback="() => setIsOpen(true)"
            :icon="ClockIcon"
          />
        </template>
      </SaveAppointmentFrequencyAction>

      <SendEmailAction
        :from-options="row.original.data.appointmentEmailFromOptions"
        :to-options="row.original.data.appointmentEmailToOptions"
        :to="row.original.data.appointmentEmailToOptions"
        :subject="row.original.data.appointmentEmailSubject"
        :body="row.original.data.appointmentEmailBody"
        :callback="sendActionCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            class="size-8"
            type="primary"
            :title="t('button.sendEmail')"
            :callback="() => setIsOpen(true)"
            :icon="EnvelopeIcon"
          />
        </template>
      </SendEmailAction>

      <ActionButton
        size="small"
        class="size-8"
        :title="t('button.see')"
        :href="
        route('front.projectManagement.apikom.appointments', {
          tenant: row.original.data.tenantId,
        })
      "
        :icon="EyeIcon"
      />
    </template>
  </div>
</template>
